import {
  ChComboBoxRenderCustomEvent,
  ComboBoxModel
} from "@genexus/chameleon-controls-library";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import {
  Component,
  Element,
  getAssetPath,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";
import { Locale } from "../../common/locale";
import { KBVersion } from "../team-dev/version-control/common/types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "utils/elevation",
  "chameleon/scrollbar"
];

const SHOW_MORE_ICON = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "interactive"
});

const VERSION_FROZEN_OBJECT_ICON = getIconPath({
  category: "objects",
  name: "version-frozen"
});

const VERSION_OBJECT_ICON = getIconPath({
  category: "objects",
  name: "version"
});

const VERSION_ICON = getIconPath({
  category: "system",
  name: "version",
  colorType: "on-surface"
});

// Uncomment this when the sync environments feature is available
// const CHECKBOX_CHECKED_VALUE = "true";

@Component({
  tag: "gx-ide-restore-version",
  styleUrl: "restore-version.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/restore-version"]
})
export class GxIdeRestoreVersion {
  #componentLocale: any;
  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/restore-version/shortcuts.json`
  );
  @Element() el: HTMLGxIdeRestoreVersionElement;

  /**
   * Indicates if the environments should be synchronized.
   */
  @State() syncEnvironments: boolean = false;

  /**
   * Input value for the source version selector.
   */
  @State() selectedSourceVersionName: string = "";

  /**
   * Selected source version from version selector.
   */
  @State() selectedSourceVersion: KBVersion[] = [];

  /**
   * Combo box model for the versions selector.
   */
  @State() versionsComboBoxModel: ComboBoxModel = [];

  /**
   *  Callback invoked by pressing the ellipsis in the version selector.
   */
  @Prop() readonly selectSourceVersionCallback!: () => Promise<KBVersion[]>;

  /**
   * Callback invoked by pressing the Confirm button.
   */
  @Prop() readonly confirmCallback!: (
    restoreVersionData: RestoreVersionData
  ) => Promise<void>;

  /**
   * Callback invoked by pressing the Cancel button.
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * The target version to which the restore will be made.
   */
  @Prop() readonly toVersionInfo!: KBVersion;

  /**
   * List of versions to show in the versions combo box.
   */
  @Prop() readonly versions!: KBVersion[];
  @Watch("versions")
  versionsChanged() {
    this.#versionsToComboBoxItemModel(this.versions);
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#versionsToComboBoxItemModel(this.versions || []);
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #confirmHandler = async () => {
    const restoreVersionData = {
      toVersionGuid: this.toVersionInfo.guid,
      toVersionName: this.toVersionInfo.name,
      fromVersionGuid: this.versions.find(
        version => version.name === this.selectedSourceVersionName
      ).guid,
      fromVersionName: this.selectedSourceVersionName,
      syncEnvironments: this.syncEnvironments
    };
    await this.confirmCallback(restoreVersionData);
  };

  #cancelHandler = async () => {
    await this.cancelCallback();
  };

  #versionsToComboBoxItemModel = (versions: KBVersion[]) => {
    this.versionsComboBoxModel = versions.map(version => ({
      caption: version.name,
      value: version.guid,
      startImgSrc: version.isFrozen
        ? VERSION_FROZEN_OBJECT_ICON
        : VERSION_OBJECT_ICON
    }));
  };

  #handleComboBoxInput = (
    event: ChComboBoxRenderCustomEvent<string> | InputEvent
  ) => {
    this.selectedSourceVersionName = event.detail as string;
  };
  // Uncomment this when the sync environments feature is available
  // #handleCheckBoxValueChange = (
  //   event: ChCheckboxCustomEvent<any> | InputEvent
  // ) => {
  //   this.syncEnvironments = event.detail === CHECKBOX_CHECKED_VALUE;
  // };

  #selectSourceVersionHandler = async () => {
    this.selectedSourceVersion = await this.selectSourceVersionCallback();
    this.selectedSourceVersionName = this.selectedSourceVersion[0].name;
  };

  #renderFooter = () => {
    const disableConfirmButton =
      !this.selectedSourceVersionName ||
      !this.versionsComboBoxModel.some(
        v => v.caption === this.selectedSourceVersionName
      );
    return (
      <footer class="footer dialog-footer dialog-footer-with-border spacing-body">
        <div class="buttons-spacer">
          <button
            id="cancel-button"
            class="button-secondary"
            onClick={this.#cancelHandler}
          >
            {this.#componentLocale.footer.cancel}
          </button>

          <button
            id="confirm-button"
            class="button-primary"
            onClick={this.#confirmHandler}
            disabled={disableConfirmButton}
          >
            {this.#componentLocale.footer.confirm}
          </button>
        </div>
      </footer>
    );
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <div class="main-container spacing-body">
          <p class="giu-static-pill-icon-and-text elevation-2">
            <ch-image
              class="icon-m"
              type="mask"
              src={VERSION_ICON}
            ></ch-image>
            <span>{`${this.#componentLocale.descriptions.pill} - ${this.toVersionInfo.name}`}</span>
          </p>
          <p class="body-regular-m">
            {this.#componentLocale.descriptions.summary}
          </p>

          <div class="field field-block">
            <label class="label" htmlFor="versions-combo-box">
              {this.#componentLocale.labels.fromVersion}
            </label>
            <div class="versions-container">
              <ch-combo-box-render
                class="combo-box"
                id="versions-combo-box"
                model={this.versionsComboBoxModel}
                placeholder={this.#componentLocale.placeHolders.fromVersion}
                value={this.selectedSourceVersionName}
                suggest={true}
                onInput={this.#handleComboBoxInput}
              />
              <button
                id="select-version-button"
                class="button-secondary button-icon-only"
                onClick={this.#selectSourceVersionHandler}
              >
                <ch-image src={SHOW_MORE_ICON} class="icon-m"></ch-image>
              </button>
            </div>
          </div>

          {
            // Uncomment this when the sync environments feature is available
            /* <ch-checkbox
            class="checkbox"
            caption={this.#componentLocale.buttons.syncEnvironments}
            checkedValue={CHECKBOX_CHECKED_VALUE}
            onInput={this.#handleCheckBoxValueChange}
          /> */
          }
        </div>
        {this.#renderFooter()}
      </Host>
    );
  }
}

export type RestoreVersionData = {
  fromVersionName: string;
  fromVersionGuid: string;
  toVersionGuid: string;
  toVersionName: string;
  syncEnvironments: boolean;
};
